import { API_URLS } from "../../../../apiUrl"
import axiosInstance from "../../../../components/axiosInstance"
import CustomSelect from "../../../../components/CustomSelect"
import { useState, useEffect, useRef } from "react"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Pagination from "@mui/material/Pagination";
import moment from "moment"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"


// ==================== Type Definitions ====================

interface Municipality {
    primary_id: string
    municipality_name: string
}

interface Borough {
    borough_primary_id: string
    borough_name: string
}

interface Ward {
    ward_primary_id: string
    ward_no: string
}

interface Street {
    street_primary_id: string
    street_name: string
}

interface Consumer {
    id: number
    connection_number: string
    consumer_name: string
    phone_number: string
    consumer_category_name: string
    ferule_size: string
    opening_due_amount: number
    status: string
}

interface DemandDraftStatus {
    id: number
    dd_allotment_number: string
    generation_started_on: string
    estimated_time_of_completion: string
    execution_time: string
    size_of_generated_pdf: string | null
    is_downloaded: boolean
    status: 'pending' | 'processing' | 'success' | 'completed' | 'failed'
    downloaded_at: string | null
    municipality_id: number
    ward_id: number
    borough_id: number
    street_id: number
    municipality_name: string
    borough_name: string
    ward_no: string
    street_name: string
    file_id?: number
    file_name?: string
}

interface DemandDraftStatusApiResponse {
    statusCode: number
    data: {
        rows: DemandDraftStatus[]
        pagination: {
            page: number
            limit: number
            lastPage: number
            totalValues: string
        }
    }
    message: string
    error: boolean
}

interface DemandDraftGenerateResponse {
    statusCode?: number
    data?: any
    message?: string
    success?: boolean
    error?: boolean
}

interface GenerateSelectedPayload {
    id: number[]
    municipality_id: number
    ward_id: number
    borough_id: number
    street_id: number
}

interface GenerateAllPayload {
    id: string
    municipality_id: number
    ward_id: number
    street_id: number
    borough_id: number
}

interface StatusPayload {
    municipality_id: number
    ward_id: number
    street_id: number
    borough_id: number
    page: number
    limit: number
}

const AllotmentCell = ({ value }: { value: string }) => {
    const [showTooltip, setShowTooltip] = useState(false)
    const btnRef = useRef<HTMLButtonElement>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)

    const truncated = value ? `${value.slice(0, 8)}...` : '-'

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node) &&
                btnRef.current && !btnRef.current.contains(event.target as Node)) {
                setShowTooltip(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (showTooltip && tooltipRef.current && btnRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            if (tooltipRect.right > viewportWidth) {
                tooltipRef.current.style.left = 'auto'
                tooltipRef.current.style.right = '0'
                tooltipRef.current.style.transform = 'translateX(0)'
            } else if (tooltipRect.left < 0) {
                tooltipRef.current.style.left = '0'
                tooltipRef.current.style.transform = 'translateX(0)'
            } else {
                tooltipRef.current.style.left = '50%'
                tooltipRef.current.style.right = 'auto'
                tooltipRef.current.style.transform = 'translateX(-30%)'
            }
        }
    }, [showTooltip])

    return (
        <div className="flex items-center gap-1.5">
            <span className="font-mono text-sm text-gray-600">{truncated}</span>
            <div className="relative inline-block">
                <button
                    ref={btnRef}
                    onClick={(e) => { e.stopPropagation(); setShowTooltip(prev => !prev) }}
                    className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center hover:bg-blue-200 transition-colors focus:outline-none flex-shrink-0"
                    title="Click to see full allotment number"
                >
                    i
                </button>
                {showTooltip && (
                    <>
                        <div
                            ref={tooltipRef}
                            className="absolute z-50 bg-gray-900 text-white text-xs rounded-lg shadow-xl"
                            style={{
                                bottom: "100%", left: "50%", transform: "translateX(-50%)",
                                marginBottom: "10px", minWidth: "max-content",
                                maxWidth: "min(90vw, 400px)", width: "auto",
                                whiteSpace: "normal", wordBreak: "break-word"
                            }}
                        >
                            <div className="px-3 py-2">
                                <p className="font-semibold text-gray-300 mb-1 text-[10px] uppercase tracking-wide text-center">Full Allotment Number</p>
                                <p className="font-mono text-xs text-center break-all">{value}</p>
                            </div>
                        </div>
                        <div
                            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }}
                            onClick={() => setShowTooltip(false)}
                        />
                    </>
                )}
            </div>
        </div>
    )
}


// ==================== Static Bill Data ====================
const staticBillData = [
    {
        bill_generation_date: "2026-04-27",
        consumer_category_name: "Domestic",
        existing_bill_amount: "120",
        ferule_size: "15mm",
        last_month_bill_amount: "80",
        mouza_name: "Tamluk",
        name: "Sumon Chakraborty",
        total_bill: "200",
        total_bill_in_words: "Two Hundred Only",
        ward_name: "24",
        water_connection_bill_no: "WB12345",
        water_connection_no: "CONN001"
    }
]


const DemandDraft = () => {
    const [municipality, setMunicipality] = useState<Municipality[]>([])
    const [borough, setBorough] = useState<Borough[]>([])
    const [ward, setWard] = useState<Ward[]>([])
    const [street, setStreet] = useState<Street[]>([])

    const [selectedMunicipality, setSelectedMunicipality] = useState<string>("")
    const [selectedBorough, setSelectedBorough] = useState<string>("")
    const [selectedWard, setSelectedWard] = useState<string>("")
    const [selectedStreet, setSelectedStreet] = useState<string>("")

    const [loadingBorough, setLoadingBorough] = useState<boolean>(false)
    const [loadingWard, setLoadingWard] = useState<boolean>(false)
    const [loadingStreet, setLoadingStreet] = useState<boolean>(false)

    const [collectors, setCollectors] = useState<Consumer[]>([])
    const [loadingTable, setLoadingTable] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [itemsPerPage] = useState<number>(50)
    const [searchTerm, setSearchTerm] = useState<string>("")
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [loadingGenerate, setLoadingGenerate] = useState<boolean>(false)
    const [isMunicipalityAdmin, setIsMunicipalityAdmin] = useState<boolean>(false)

    const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false)
    const [showProgressPopup, setShowProgressPopup] = useState<boolean>(false)
    const [demandDraftStatus, setDemandDraftStatus] = useState<DemandDraftStatus[]>([])
    const [loadingStatus, setLoadingStatus] = useState<boolean>(false)
    const [downloadingFile, setDownloadingFile] = useState<number | null>(null)

    const [totalCount, setTotalCount] = useState<number>(0)
    const [totalPages, setTotalPages] = useState<number>(0)
    const [allConsumerIds, setAllConsumerIds] = useState<number[]>([])
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)

    // ── PDF download loading state ──
    const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false)

    const [ddPage, setDdPage] = useState<number>(1)
    const [ddLimit] = useState<number>(10)
    const [ddTotalPages, setDdTotalPages] = useState<number>(1)
    const [ddTotalCount, setDdTotalCount] = useState<number>(0)

    const roleType = localStorage.getItem('role_type')
    const MunicipalityID = localStorage.getItem('municipality_id_v2')
    const MunicipalityName = localStorage.getItem('municipality_name_v2')

    const formatLocalDateTime = (dateString?: string): string => {
        if (!dateString) return '-'
        const m = moment(dateString)
        if (!m.isValid()) return '-'
        return m.local().format('DD/MM/YYYY, hh:mm A')
    }

    const formatExecutionTime = (value?: string): string => {
        if (!value) return '-'
        const match = value.match(/[\d.]+/)
        if (!match) return '-'
        const ms = Number(match[0])
        if (isNaN(ms)) return '-'
        if (ms < 1000) return `${(ms / 1000).toFixed(2)}s`
        const seconds = ms / 1000
        if (seconds >= 60) return `${Math.floor(seconds / 60)} min`
        return `${seconds.toFixed(0)} sec`
    }

    const resolveMunicipalityId = (): string => {
        if (selectedMunicipality) return selectedMunicipality
        if (isMunicipalityAdmin && MunicipalityID) return String(MunicipalityID)
        return ""
    }

    const validateFields = (): boolean => {
        const mv = resolveMunicipalityId()
        if (!mv || !selectedBorough || !selectedWard || !selectedStreet) {
            toast.error("Please select all fields (Municipality, Borough, Ward, Street)")
            return false
        }
        return true
    }

    const fetchMunicipalityBorough = async (municipalityId: number): Promise<void> => {
        setLoadingBorough(true)
        setBorough([])
        setSelectedBorough("")
        setWard([])
        setSelectedWard("")
        setStreet([])
        setSelectedStreet("")
        setCollectors([])
        setSelectedIds([])
        setSelectAllChecked(false)
        setAllConsumerIds([])
        try {
            const res = await axiosInstance.post(`${API_URLS.GetMunicipalityBorough}`, {
                municipalityTbl_id: municipalityId, page: 1, limit: 50, surveyer_id: 0
            })
            const data: Borough[] = res.data?.data?.data || []
            setBorough(data)
            if (data.length === 0) toast.info("No boroughs found")
        } catch {
            toast.error("Failed to fetch borough data")
        } finally {
            setLoadingBorough(false)
        }
    }

    const fetchBoroughWiseWard = async (municipalityId: number, boroughId: string): Promise<void> => {
        setLoadingWard(true)
        setWard([])
        setSelectedWard("")
        setStreet([])
        setSelectedStreet("")
        setCollectors([])
        setSelectedIds([])
        setSelectAllChecked(false)
        setAllConsumerIds([])
        try {
            const res = await axiosInstance.post(`${API_URLS.GetBoroughWiseWard}`, {
                municipalityTbl_id: municipalityId, borough_primary_id: String(boroughId),
                page: 1, limit: 50, surveyer_id: 0
            })
            const data: Ward[] = res.data?.data?.data || []
            setWard(data)
            if (data.length === 0) toast.info("No wards found")
        } catch {
            toast.error("Failed to fetch ward data")
        } finally {
            setLoadingWard(false)
        }
    }

    const fetchStreetWiseWard = async (municipalityId: number, wardId: string): Promise<void> => {
        setLoadingStreet(true)
        setStreet([])
        setSelectedStreet("")
        setCollectors([])
        setSelectedIds([])
        setSelectAllChecked(false)
        setAllConsumerIds([])
        try {
            const res = await axiosInstance.post(`${API_URLS.streetWordWise}`, {
                municipalityTbl_id: municipalityId, ward_primary_id: String(wardId),
                page: 1, limit: 50, surveyer_id: 0
            })
            const data: Street[] = res.data?.data?.data || []
            setStreet(data)
            if (data.length === 0) toast.info("No streets found")
        } catch {
            toast.error("Failed to fetch street data")
        } finally {
            setLoadingStreet(false)
        }
    }

    const fetchAllConsumerIds = async (streetId: string, municipalityId: string): Promise<number[]> => {
        try {
            const firstRes = await axiosInstance.post(API_URLS.consumerDetail, {
                page: 1, limit: 1,
                street_id: Number(streetId), municipality_id: Number(municipalityId),
                search: searchTerm.trim()
            })
            const total = firstRes?.data?.data?.pagination?.totalValues ||
                firstRes?.data?.pagination?.totalValues || 0
            if (total === 0) return []

            const allIds: number[] = []
            const chunkSize = 500
            const totalPagesCount = Math.ceil(total / chunkSize)

            for (let i = 1; i <= totalPagesCount; i++) {
                const res = await axiosInstance.post(API_URLS.consumerDetail, {
                    page: i, limit: chunkSize,
                    street_id: Number(streetId), municipality_id: Number(municipalityId),
                    search: searchTerm.trim()
                })
                const rows: Consumer[] = res?.data?.data?.rows || res?.data?.rows || []
                allIds.push(...rows.map((item: Consumer) => item.id))
            }
            return allIds
        } catch (error) {
            console.error("Error fetching all consumer IDs:", error)
            return []
        }
    }

    const fetchCollectors = async (
        streetId?: string, municipalityId?: string,
        search: string = "", page: number = 1
    ): Promise<void> => {
        const sv = streetId ?? selectedStreet
        const mv = municipalityId ?? resolveMunicipalityId()
        if (!sv || !mv) { toast.error("Please select both street and municipality"); return }

        setLoadingTable(true)
        setCollectors([])
        try {
            const res = await axiosInstance.post(API_URLS.consumerDetail, {
                page, limit: itemsPerPage,
                street_id: Number(sv), municipality_id: Number(mv), search: search.trim()
            })
            const rows: Consumer[] = res?.data?.data?.rows || res?.data?.rows || []
            const total = res?.data?.data?.pagination?.totalValues ||
                res?.data?.pagination?.totalValues || res?.data?.data?.count ||
                res?.data?.count || rows.length
            const lastPage = res?.data?.data?.pagination?.lastPage ||
                res?.data?.pagination?.lastPage || Math.ceil(total / itemsPerPage)

            setCollectors(rows)
            setTotalCount(total)
            setTotalPages(lastPage)

            if (rows.length > 0 && allConsumerIds.length === 0) {
                const allIds = await fetchAllConsumerIds(sv, mv)
                setAllConsumerIds(allIds)
            }
            if (rows.length === 0) {
                toast.info(search ? "No matching consumers found" : "No consumers found for this street")
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Failed to fetch consumer data")
        } finally {
            setLoadingTable(false)
        }
    }

    const generateDemandDraft = async (): Promise<void> => {
        if (selectedIds.length === 0) { toast.error("Please select at least one consumer"); return }
        if (!validateFields()) return

        const mv = resolveMunicipalityId()
        setLoadingGenerate(true)
        try {
            const payload: GenerateSelectedPayload = {
                id: selectedIds, municipality_id: Number(mv),
                ward_id: Number(selectedWard), borough_id: Number(selectedBorough),
                street_id: Number(selectedStreet)
            }
            const res = await axiosInstance.post<DemandDraftGenerateResponse>(
                `${API_URLS.DemandDraftGenerateapi}`, payload
            )
            const directData = res.data?.data?.rows || res.data?.data
            if (directData && Array.isArray(directData) && directData.length > 0) {
                toast.success(`Demand draft generated for ${selectedIds.length} consumer(s)`)
                setSelectedIds([])
                setSelectAllChecked(false)
                return
            }
            if (directData && typeof directData === 'object' && !Array.isArray(directData)) {
                toast.success(`Demand draft generated for ${selectedIds.length} consumer(s)`)
                setSelectedIds([])
                setSelectAllChecked(false)
                return
            }
            toast.info(res.data?.message || "...", { autoClose: 5000 })
            setSelectedIds([])
            setSelectAllChecked(false)
            await handleOpenProgressPopup()
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to generate demand draft")
        } finally {
            setLoadingGenerate(false)
        }
    }

    const generateAllDemandDraft = async (): Promise<void> => {
        if (!validateFields()) return
        const mv = resolveMunicipalityId()
        setLoadingGenerate(true)
        try {
            const payload: GenerateAllPayload = {
                id: "all", municipality_id: Number(mv),
                street_id: Number(selectedStreet), ward_id: Number(selectedWard),
                borough_id: Number(selectedBorough)
            }
            const res = await axiosInstance.post<DemandDraftGenerateResponse>(
                `${API_URLS.DemandDraftGenerateapi}`, payload
            )
            const directData = res.data?.data?.rows || res.data?.data
            if (directData && Array.isArray(directData) && directData.length > 0) {
                toast.success("Demand draft generated for all consumers")
                return
            }
            toast.info(
                res.data?.message || "Demand draft generation started for all consumers! Track progress from the popup.",
                { autoClose: 5000 }
            )
            await handleOpenProgressPopup()
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to generate demand draft for all consumers")
        } finally {
            setLoadingGenerate(false)
        }
    }

    const fetchDemandDraftStatus = async (page: number = ddPage): Promise<void> => {
        if (!validateFields()) return
        const mv = resolveMunicipalityId()
        setLoadingStatus(true)
        try {
            const payload: StatusPayload = {
                municipality_id: Number(mv), ward_id: Number(selectedWard),
                street_id: Number(selectedStreet), borough_id: Number(selectedBorough),
                page, limit: ddLimit,
            }
            const res = await axiosInstance.post<DemandDraftStatusApiResponse>(
                `${API_URLS.DemandDraftStatusapi}`, payload
            )
            const rows: DemandDraftStatus[] = res.data?.data?.rows || []
            const pagination = res.data?.data?.pagination
            setDemandDraftStatus(rows)
            setDdTotalPages(pagination?.lastPage ?? 1)
            setDdTotalCount(Number(pagination?.totalValues ?? 0))
            if (rows.length === 0) toast.info("No demand draft records found")
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to fetch demand draft status")
        } finally {
            setLoadingStatus(false)
        }
    }

    const downloadByAllotmentNumber = async (item: DemandDraftStatus): Promise<void> => {
        setDownloadingFile(item.id)
        try {
            const response = await axiosInstance.post<Blob>(
                `${API_URLS.DemandDraftDownloadapi}`,
                { dd_allotment_number: item.dd_allotment_number, ...(item.file_id ? { file_id: item.file_id } : {}) },
                { responseType: 'blob' }
            )
            const fileName = item.file_name || `demand_draft_${item.dd_allotment_number}.pdf`
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            setDemandDraftStatus(prev =>
                prev.map(d => d.id === item.id ? { ...d, is_downloaded: true } : d)
            )
            toast.success("File downloaded successfully")
        } catch (err: any) {
            let errorMessage = "Failed to download file"
            if (err?.response?.data instanceof Blob) {
                try {
                    const errorText = await err.response.data.text()
                    const errorJson = JSON.parse(errorText)
                    errorMessage = errorJson.message || errorMessage
                } catch { errorMessage = err.response.statusText || errorMessage }
            } else if (err?.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err?.message) {
                errorMessage = err.message
            }
            toast.error(errorMessage)
        } finally {
            setDownloadingFile(null)
        }
    }

    const handleOpenProgressPopup = async (): Promise<void> => {
        if (!validateFields()) return
        setDdPage(1)
        const mv = resolveMunicipalityId()
        setLoadingStatus(true)
        try {
            const payload: StatusPayload = {
                municipality_id: Number(mv), ward_id: Number(selectedWard),
                street_id: Number(selectedStreet), borough_id: Number(selectedBorough),
                page: 1, limit: ddLimit,
            }
            const res = await axiosInstance.post<DemandDraftStatusApiResponse>(
                `${API_URLS.DemandDraftStatusapi}`, payload
            )
            const rows: DemandDraftStatus[] = res.data?.data?.rows || []
            const pagination = res.data?.data?.pagination
            setDemandDraftStatus(rows)
            setDdTotalPages(pagination?.lastPage ?? 1)
            setDdTotalCount(Number(pagination?.totalValues ?? 0))
            setShowProgressPopup(true)
            if (rows.length === 0) toast.info("No demand draft records found")
        } catch (err: any) {
            let errorMessage = "Failed to fetch demand draft status"
            if (err?.response?.data) {
                if (err.response.data instanceof Blob) {
                    try {
                        const errorText = await err.response.data.text()
                        const errorJson = JSON.parse(errorText)
                        errorMessage = errorJson.message || errorMessage
                    } catch { errorMessage = err.response.statusText || errorMessage }
                } else if (typeof err.response.data === 'object' && err.response.data.message) {
                    errorMessage = err.response.data.message
                } else if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data
                }
            } else if (err?.message) {
                errorMessage = err.message
            }
            toast.error(errorMessage)
        } finally {
            setLoadingStatus(false)
        }
    }

    const handleDdPageChange = async (_: React.ChangeEvent<unknown>, value: number): Promise<void> => {
        setDdPage(value)
        await fetchDemandDraftStatus(value)
    }

    const handleClosePopup = (): void => {
        setShowProgressPopup(false)
        setDemandDraftStatus([])
        setDdPage(1)
        setDdTotalPages(1)
        setDdTotalCount(0)
    }

    const handleSelectOne = (id: number): void => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    }

    const handleSelectAllCheckbox = (): void => {
        if (selectAllChecked) {
            const currentPageIds = collectors.map((c: Consumer) => c.id)
            setSelectedIds(prev => prev.filter((id: number) => !currentPageIds.includes(id)))
        } else {
            const currentPageIds = collectors.map((c: Consumer) => c.id)
            setSelectedIds(prev => {
                const newIds = [...prev]
                currentPageIds.forEach(id => { if (!newIds.includes(id)) newIds.push(id) })
                return newIds
            })
        }
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value
        setSearchTerm(value)
        setCurrentPage(1)
        setAllConsumerIds([])
        setSelectedIds([])
        if (searchTimeout) clearTimeout(searchTimeout)
        const timeout = setTimeout(() => {
            if (selectedStreet && selectedMunicipality) {
                fetchCollectors(selectedStreet, selectedMunicipality, value, 1)
            }
        }, 500)
        setSearchTimeout(timeout)
    }

    const handleMunicipalityChange = (e: any): void => {
        const v = e.target.value
        setSelectedMunicipality(v)
        if (v) fetchMunicipalityBorough(Number(v))
    }

    const handleBoroughChange = (e: any): void => {
        const v = e.target.value
        setSelectedBorough(v)
        if (v && selectedMunicipality) fetchBoroughWiseWard(Number(selectedMunicipality), v)
    }

    const handleWardChange = (e: any): void => {
        const v = e.target.value
        setSelectedWard(v)
        if (v && selectedMunicipality) fetchStreetWiseWard(Number(selectedMunicipality), v)
    }

    const handleStreetChange = (e: any): void => {
        const v = e.target.value
        setSelectedStreet(v)
        setCurrentPage(1)
        setSearchTerm("")
        setSelectedIds([])
        setSelectAllChecked(false)
        setAllConsumerIds([])
        if (v && selectedMunicipality) fetchCollectors(v, selectedMunicipality, "", 1)
    }

    const handlePageChange = (page: number): void => {
        setCurrentPage(page)
        if (selectedStreet && selectedMunicipality) {
            fetchCollectors(selectedStreet, selectedMunicipality, searchTerm, page)
        }
    }

   
 const getMunicipalityDisplayName = (): string | null => {
        if (isMunicipalityAdmin && MunicipalityName) return MunicipalityName
        return null
    }

    const handleClearSearch = (): void => {
        setSearchTerm("")
        setCurrentPage(1)
        setAllConsumerIds([])
        setSelectedIds([])
        if (selectedStreet && resolveMunicipalityId()) {
            fetchCollectors(selectedStreet, resolveMunicipalityId(), "", 1)
        }
    }

const generateAndDownloadPDF = async (billsData: any[]): Promise<void> => {
    setDownloadingPdf(true)
    try {
        const f = (v: any): string => (v == null || v === "" || v === "null" ? "" : String(v))

        const oneBill = (data: any): string => {
            const d = data?.data || data
            const formatDate = (dateValue: any): string => {
                if (!dateValue) return ''
                const date = new Date(dateValue)
                if (isNaN(date.getTime())) return String(dateValue)
                const day = String(date.getDate()).padStart(2, '0')
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const year = date.getFullYear()
                return `${day}/${month}/${year}`
            }
            const billgendate            = formatDate(d?.bill_generation_date)
            const catagory               = f(d?.consumer_category_name)
            const existing_bill_amount   = f(d?.existing_bill_amount)
            const farrul                 = f(d?.ferule_size)
            const last_month_bill_amount = f(d?.last_month_bill_amount)
            const mouza_name             = f(d?.mouza_name)
            const name                   = f(d?.name)
            const total_bill             = f(d?.total_bill)
            const total_bill_in_words    = f(d?.total_bill_in_words)
            const ward_name              = f(d?.ward_name)
            const water_connection_bill_no = f(d?.water_connection_bill_no)
            const water_connection_no    = f(d?.water_connection_no)

            return `
<table width="100%" cellpadding="0" cellspacing="0" style="margin: auto; border-bottom: 1px dashed #bbb; margin-bottom: 0px; padding-bottom: 0px;">
    <tr>
        <!-- ======== LEFT COUPON ======== -->
        <td width="45%" valign="top" style="padding: 2px 3px; padding-top:4px !important; border-right: 1px dashed #bbb;">
            <table width="100%" cellspacing="0" cellpadding="2">
                <tr>
                    <td colspan="2" style="font-size: 7px; color: #FF671F;">Form No. 41(as per Rules 110 and 116)</td>
                    <td align="right" style="font-size: 7px; font-weight: bold; color: #046A38;">Office Transcript</td>
                </tr>
                <tr>
                    <td colspan="3" align="center" style="font-size: 14px; color: #06038D; font-weight: bold; padding: 2px 0;">
                        Tamralipta Municipality
                    </td>
                </tr>
                <tr><td colspan="3" style="height:5px"></td></tr>
                <tr>
                    <td colspan="3" align="center" style="font-size: 12px; color: #fff; background-color: #046A38; padding: 3px 0; height: 25px; vertical-align: middle; padding-bottom:15px;">
                        Miscellaneous Bills (Water Department)
                    </td>
                </tr>
                <tr>
                    <td style="font-size: 8px; color: #06038D; white-space: nowrap;">Bill No- ${billgendate}</td>
                    <td colspan="2" style="font-size: 8px; color: #06038D; text-align: center; white-space: nowrap;">
                        Date of issue of Bill- ${billgendate}
                    </td>
                </tr>
                <tr>
                    <td style="font-size: 8px; color: #06038D; white-space: nowrap;">Class- ${catagory}</td>
                    <td style="font-size: 8px; color: #06038D; text-align: center; white-space: nowrap;">Ward No- ${ward_name}</td>
                    <td style="font-size: 8px; color: #06038D; text-align: right; white-space: nowrap;">Ferrule size- ${farrul}</td>
                </tr>
                <tr>
                    <td colspan="2" style="font-size: 8px; color: #06038D; white-space: nowrap;">Connection No- ${water_connection_no}</td>
                    <td style="font-size: 8px; color: #06038D; text-align: right;">for this</td>
                </tr>
                <tr>
                    <td colspan="3" style="font-size: 8px; color: #06038D;">Name- ${name}</td>
                </tr>
                <tr>
                    <td colspan="3" style="font-size: 8px; color: #06038D;">Mouza- ${mouza_name}</td>
                </tr>
                <tr>
                    <td style="font-size: 8px; color: #06038D; white-space: nowrap;">Due Water Tax- ${existing_bill_amount}</td>
                    <td colspan="2" style="font-size: 8px; color: #06038D; text-align: right;">
                        Water tax due for the current month- ${last_month_bill_amount}
                    </td>
                </tr>
                <tr>
                    <td colspan="3" style="font-size: 8px; color: #06038D;">
                       Total- ${total_bill}<br/>(in words) ${total_bill_in_words}
                    </td>
                </tr>
                <tr style="height: 80px;">
                    <td style="font-size: 8px; color: #FF671F; vertical-align: bottom;">Date-</td>
                    <td style="font-size: 8px; color: #046A38; text-align: center; vertical-align: bottom;">
                        <hr style="border: none; border-top: 1px solid #046A38; margin: 0 auto 2px; width: 90px;" />
                        Customer's Signature
                    </td>
                    <td style="font-size: 8px; color: #046A38; text-align: center; vertical-align: bottom;">
                        <hr style="border: none; border-top: 1px solid #046A38; margin: 0 auto 2px; width: 90px;" />
                        Deputy Mayor
                    </td>
                </tr>
            </table>
        </td>

        <!-- ======== RIGHT COUPON ======== -->
        <td width="55%" valign="top" style="padding: 2px 3px; border-right: 1px dashed #bbb;">
            <table width="100%" cellspacing="0" cellpadding="2">
                <tr>
                    <td colspan="4" style="font-size: 7px; color: #FF671F;">Form No. 41(as per Rules 110 and 116)</td>
                </tr>
                <tr>
                    <td colspan="4" align="center" style="font-size: 14px; color: #06038D; font-weight: bold; padding: 2px 0;">
                        Tamralipta Municipality
                    </td>
                </tr>
                <tr><td colspan="3" style="height:5px"></td></tr>
                <tr>
                    <td colspan="4" align="center" style="font-size: 12px; color: #fff; background-color: #046A38; padding: 3px 0; padding-bottom:15px; vertical-align: middle; line-height: initial;">
                        Miscellaneous Bills (Water Department)
                    </td>
                </tr>
                <tr>
                    <td style="font-size: 8px; color: #06038D; white-space: nowrap;">Bill No- ${water_connection_bill_no}</td>
                    <td colspan="2" style="font-size: 8px; color: #06038D; white-space: nowrap;">Date of issue of bill- ${billgendate}</td>
                    <td style="font-size: 8px; color: #06038D; text-align: right; white-space: nowrap;">Connection No- ${water_connection_no}</td>
                </tr>
                <tr>
                    <td style="font-size: 8px; color: #06038D; white-space: nowrap;">Class- ${catagory}</td>
                    <td style="font-size: 8px; color: #06038D; white-space: nowrap;">Ward No- ${ward_name}</td>
                    <td style="font-size: 8px; color: #06038D; white-space: nowrap;">Ferrule size- ${farrul}</td>
                    <td style="font-size: 8px; color: #06038D; text-align: right;">for this</td>
                </tr>
                <tr>
                    <td colspan="3" style="font-size: 8px; color: #06038D;">Mouza- ${mouza_name}</td>
                    <td style="font-size: 8px; color: #046A38; text-align: right;">Deputy Mayor</td>
                </tr>
                <tr>
                    <td colspan="4" style="padding-top: 4px;">
                        <table width="100%" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">
                            <tr>
                                <td style="font-size: 8px; color: #06038D; text-align: right;
                                           border-left: 1px solid #FF671F; border-top: 1px solid #FF671F; border-bottom: 1px solid #FF671F; padding: 2px 5px; padding-bottom:13px !important;">
                                    Arrears of water tax
                                </td>
                                <td style="font-size: 8px; color: #FF671F; text-align: right;
                                           border: 1px solid #FF671F; width: 75px; padding: 3px 5px; padding-bottom:13px !important;">
                                    ${existing_bill_amount}
                                </td>
                            </tr>
                            <tr>
                                <td style="font-size: 8px; color: #06038D; text-align: right; border:none;
                                           border-left: 1px solid #FF671F; padding: 3px 5px; padding-bottom:13px !important;">
                                    Water tax due for the current month
                                </td>
                                <td style="font-size: 8px; color: #FF671F; text-align: right;
                                           border-left: 1px solid #FF671F; border-right: 1px solid #FF671F; width: 75px; padding: 3px 5px; padding-bottom:13px !important;">
                                    ${last_month_bill_amount}
                                </td>
                            </tr>
                            <tr>
                                <td style="font-size: 8px; color: #06038D;
                                           border: 1px solid #FF671F; padding: 3px 5px; padding-bottom:13px !important;">
                                    (in words) ${total_bill_in_words}
                                </td>
                                <td style="font-size: 8px; color: #06038D; text-align: right;
                                           border: 1px solid #FF671F; background-color: #94ffcb; width: 75px; padding: 3px 5px; padding-bottom:13px !important;">
                                    Total ${total_bill}
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
                <tr>
                    <td colspan="4" style="font-size: 8px; color: #333; border: 1px solid #046A38; padding-bottom:13px !important;">
                        <span style="color: #FF671F; font-weight: 700;">Warning Notice-</span>
                        (1) If the water bill is outstanding for three consecutive months, the water line may be disconnected.
                        (2) It is mandatory to submit the water bill to the municipal office by the 15th of every month.
                        (3) Do not install a motor or pump machine directly on the water line. If such an incident comes to the notice of the municipal authorities, the water line will be cut off.
                        (4) Use a stop cock to prevent overflow in the water tank of the house.
                        </br>
                        <span style="color: #FF671F; font-weight: 700; font-size: 8px;">(5) Come to the municipality office and pay the water tax or pay it to the collector.</span>
                    </td>
                </tr>
            </table>
        </td>
    </tr>
</table>`
        }

        const pages: string[] = []
        for (let i = 0; i < billsData.length; i += 3) {
            const chunk = billsData.slice(i, i + 3)
            pages.push(`
                <div style="width:210mm; padding:6mm; box-sizing:border-box; background:#fff;">
                    ${chunk.map(oneBill).join("")}
                </div>
            `)
        }

        const container = document.createElement("div")
        container.innerHTML = pages.join("")
        container.style.position = "fixed"
        container.style.top = "0"
        container.style.left = "210mm"       
        container.style.width = "210mm"
        container.style.background = "#fff"
        container.style.zIndex = "-9999"
        document.body.appendChild(container)

        const pdf = new jsPDF("p", "mm", "a4")
        const pageElements = Array.from(container.children) as HTMLElement[]

        for (let i = 0; i < pageElements.length; i++) {
            const page = pageElements[i]

            await new Promise<void>(resolve => setTimeout(resolve, 100))

            const canvas = await html2canvas(page, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: "#ffffff",
                width: page.offsetWidth,
                height: page.offsetHeight,
                windowWidth: page.offsetWidth,
                windowHeight: page.offsetHeight,
            })

            const imgData = canvas.toDataURL("image/jpeg", 1.0)
            const imgWidth = 210
            const imgHeight = (canvas.height * imgWidth) / canvas.width

            if (i !== 0) pdf.addPage()
            pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight)
        }

        pdf.save(`demand_draft_${new Date().toISOString().split("T")[0]}.pdf`)
        document.body.removeChild(container)

    } catch (error) {
        console.error("PDF generation failed:", error)
        toast.error("PDF generation failed")
    } finally {
        setDownloadingPdf(false)
    }
}


    // ==================== Effects ====================

    useEffect(() => {
        const isMunAdmin = roleType === "Municipality Admin"
        setIsMunicipalityAdmin(isMunAdmin)
        if (isMunAdmin && MunicipalityID) {
            const municipalityId = String(MunicipalityID)
            setSelectedMunicipality(municipalityId)
            fetchMunicipalityBorough(Number(municipalityId))
        } else if (!isMunAdmin) {
            const fetchMunicipalities = async (): Promise<void> => {
                try {
                    const res = await axiosInstance.post<{ data: { data: Municipality[] } }>(
                        `${API_URLS.MunicipalityList}`, { limit: 50, page: 1 }
                    )
                    const data = res.data?.data?.data || []
                    setMunicipality(data)
                    if (data.length === 0) toast.info("No municipalities found")
                } catch {
                    toast.error("Failed to fetch municipalities")
                }
            }
            fetchMunicipalities()
        }
    }, [])

    useEffect(() => {
        if (collectors.length > 0) {
            const currentPageIds = collectors.map((c: Consumer) => c.id)
            const allSelected = currentPageIds.length > 0 &&
                currentPageIds.every((id: number) => selectedIds.includes(id))
            setSelectAllChecked(allSelected)
        } else {
            setSelectAllChecked(false)
        }
    }, [selectedIds, collectors])

    useEffect(() => {
        return () => { if (searchTimeout) clearTimeout(searchTimeout) }
    }, [searchTimeout])

    useEffect(() => {
        if (showProgressPopup) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => { document.body.style.overflow = '' }
    }, [showProgressPopup])

    // ==================== Render Helpers ====================

    const municipalityOpts = municipality.map((x: Municipality) => ({ value: x.primary_id, label: x.municipality_name }))
    const boroughOpts = borough.map((x: Borough) => ({ value: x.borough_primary_id, label: x.borough_name }))
    const wardOpts = ward.map((x: Ward) => ({ value: x.ward_primary_id, label: x.ward_no }))
    const streetOpts = street.map((x: Street) => ({ value: x.street_primary_id, label: x.street_name }))

    // ==================== JSX Return ====================

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="mb-8">
                    <div className="flex justify-between items-start">
                        <div className="flex flex-col gap-2">
                            <h1 className="text-3xl font-bold text-gray-800 border-b-4 border-green-500 pb-2 inline-block">
                                Demand Draft Form
                            </h1>

                          

                            {isMunicipalityAdmin && (
                                <div className="text-sm text-gray-600">
                                    Municipality: <span className="font-semibold text-green-700">{getMunicipalityDisplayName()}</span>
                                </div>
                            )}
                        </div>

                    <div className="flex items-center gap-4 flex-wrap">
    {/* Demand Draft in Progress Button */}
    <button
        onClick={handleOpenProgressPopup}
        disabled={!selectedStreet || !resolveMunicipalityId() || !selectedWard || !selectedBorough}
        className={`px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg transition-all flex items-center gap-2 ${
            !selectedStreet || !resolveMunicipalityId() || !selectedWard || !selectedBorough
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-purple-700"
        }`}
    >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Demand Draft in Progress
    </button>

    {/* Dummy PDF Button */}
    <button
        onClick={() => generateAndDownloadPDF(staticBillData)}
        disabled={downloadingPdf}
        className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all
            ${downloadingPdf
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white shadow-sm active:scale-95"
            }`}
    >
        {downloadingPdf ? (
            <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Generating PDF...
            </>
        ) : (
            <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Dummy PDF
            </>
        )}
    </button>
</div>
                    </div>
                </div>

                {/* Selection Form */}
                <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {!isMunicipalityAdmin && (
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Municipality <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    name="municipality"
                                    value={selectedMunicipality}
                                    onChange={handleMunicipalityChange}
                                    options={municipalityOpts}
                                    placeholder="Select Municipality"
                                    className="w-full"
                                />
                            </div>
                        )}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">
                                Borough <span className="text-red-500">*</span>
                            </label>
                            <CustomSelect
                                name="borough"
                                value={selectedBorough}
                                onChange={handleBoroughChange}
                                options={boroughOpts}
                                placeholder={loadingBorough ? "Loading..." : "Select Borough"}
                                disabled={!selectedMunicipality || loadingBorough}
                                className="w-full"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">
                                Ward <span className="text-red-500">*</span>
                            </label>
                            <CustomSelect
                                name="ward"
                                value={selectedWard}
                                onChange={handleWardChange}
                                options={wardOpts}
                                placeholder={loadingWard ? "Loading..." : "Select Ward"}
                                disabled={!selectedBorough || loadingWard}
                                className="w-full"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">
                                Street <span className="text-red-500">*</span>
                            </label>
                            <CustomSelect
                                name="street"
                                value={selectedStreet}
                                onChange={handleStreetChange}
                                options={streetOpts}
                                placeholder={loadingStreet ? "Loading..." : "Select Street"}
                                disabled={!selectedWard || loadingStreet}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* Consumer Table */}
                {selectedStreet && (
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
                            <div className="flex items-center gap-3">
                                <h2 className="text-xl font-bold text-gray-800">Consumer List</h2>
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                    {selectedIds.length} selected
                                </span>
                                {totalCount > 0 && (
                                    <span className="text-xs text-gray-500">(Total: {totalCount} consumers)</span>
                                )}
                            </div>
                            <div className="flex gap-4 flex-wrap items-center">
                                <div className="relative flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search by name, connection, phone No..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 w-80 text-sm"
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={handleClearSearch}
                                            type="button"
                                            title="Clear search"
                                            className="absolute right-2.5 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                <button
                                    onClick={generateDemandDraft}
                                    disabled={selectedIds.length === 0 || loadingGenerate}
                                    className={`px-5 py-2 bg-green-600 text-white font-semibold rounded-lg transition-all flex items-center gap-2 ${
                                        selectedIds.length === 0 || loadingGenerate ? "opacity-50 cursor-not-allowed" : "hover:bg-green-700 active:scale-95"
                                    }`}
                                >
                                    {loadingGenerate ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                            </svg>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                            </svg>
                                            Generate Draft ({selectedIds.length})
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={generateAllDemandDraft}
                                    disabled={loadingGenerate}
                                    className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg transition-all flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loadingGenerate ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                            </svg>
                                            Generating All...
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Generate All Demand Draft
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 border-b w-12">
                                            <input
                                                type="checkbox"
                                                checked={selectAllChecked}
                                                onChange={handleSelectAllCheckbox}
                                                disabled={loadingTable || collectors.length === 0}
                                                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                        </th>
                                        <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Connection No.</th>
                                        <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Consumer Name</th>
                                        <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Phone Number</th>
                                        <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Category</th>
                                        <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Ferrule Size</th>
                                        <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Opening Due</th>
                                        <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingTable ? (
                                        <tr>
                                            <td colSpan={8} className="text-center py-10 text-gray-500">
                                                <div className="flex justify-center items-center gap-2">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                                                    <span>Loading consumers...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : collectors.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="text-center py-10 text-gray-500">
                                                {searchTerm ? "No matching consumers found" : "No consumers found for this street"}
                                            </td>
                                        </tr>
                                    ) : (
                                        collectors.map((item: Consumer) => {
                                            const isChecked = selectedIds.includes(item.id)
                                            return (
                                                <tr
                                                    key={item.id}
                                                    onClick={() => handleSelectOne(item.id)}
                                                    className={`transition-colors ${isChecked ? "bg-green-50 cursor-pointer hover:bg-green-100" : "hover:bg-gray-50 cursor-pointer"}`}
                                                >
                                                    <td className="px-4 py-3 border-b">
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => handleSelectOne(item.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 border-b text-sm text-gray-700">{item.connection_number}</td>
                                                    <td className="px-4 py-3 border-b text-sm text-gray-700 font-medium">{item.consumer_name}</td>
                                                    <td className="px-4 py-3 border-b text-sm text-gray-700">{item.phone_number || "-"}</td>
                                                    <td className="px-4 py-3 border-b text-sm text-gray-700">{item.consumer_category_name}</td>
                                                    <td className="px-4 py-3 border-b text-sm text-gray-700">{item.ferule_size}</td>
                                                    <td className="px-4 py-3 border-b text-sm text-gray-700 font-medium">₹{item.opening_due_amount}</td>
                                                    <td className="px-4 py-3 border-b">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                                            {item.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
{totalPages > 1 && (
    <div className="flex justify-center items-center mt-6">
        <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, value) => handlePageChange(value)}
            color="primary"
            size="small"
        />
    </div>
)}
                    </div>
                )}
            </div>

            {/* ==================== Demand Draft Progress Popup ==================== */}
            {showProgressPopup && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onWheel={(e) => e.stopPropagation()}
                    onClick={(e) => { if (e.target === e.currentTarget) handleClosePopup() }}
                >
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden"
                        style={{ height: '80vh', maxHeight: '800px', minHeight: '500px' }}>

                        {/* Header - Fixed */}
                        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">Demand Draft Progress</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Borough: {borough.find(b => b.borough_primary_id === selectedBorough)?.borough_name || '-'} |&nbsp;
                                        Ward: {ward.find(w => w.ward_primary_id === selectedWard)?.ward_no || '-'} |&nbsp;
                                        Street: {street.find(s => s.street_primary_id === selectedStreet)?.street_name || '-'}
                                    </p>
                                </div>
                                <button
                                    onClick={handleClosePopup}
                                    className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Toolbar - Fixed */}
                        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50/50">
                            <p className="text-xs text-gray-500">
                                {ddTotalCount > 0
                                    ? <>Total records: <span className="font-semibold text-gray-700">{ddTotalCount}</span></>
                                    : <span className="invisible">placeholder</span>
                                }
                            </p>
                            <button
                                onClick={() => fetchDemandDraftStatus(ddPage)}
                                disabled={loadingStatus}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 shadow-sm text-xs font-medium"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {loadingStatus ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>

                        {/* Table area — scrolls independently */}
                        <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0">
                            {loadingStatus ? (
                                <div className="flex justify-center items-center h-full min-h-[300px]">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                                </div>
                            ) : demandDraftStatus.length === 0 ? (
                                <div className="text-center text-gray-500 flex flex-col items-center justify-center h-full min-h-[300px]">
                                    <svg className="w-14 h-14 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="font-medium">No demand draft records found</p>
                                    <p className="text-sm mt-1 text-gray-400">Generate a demand draft to see progress here</p>
                                </div>
                            ) : (
                                <table className="min-w-full bg-white">
                                    <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
                                        <tr>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Allotment No.</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Started On</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Exec. Time</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {demandDraftStatus.map((item: DemandDraftStatus, index) => (
                                            <tr key={item.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                                                <td className="px-5 py-3">
                                                    <AllotmentCell value={item.dd_allotment_number} />
                                                </td>
                                                <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap">
                                                    {formatLocalDateTime(item.generation_started_on)}
                                                </td>
                                                <td className="px-5 py-3 text-sm text-gray-700">
                                                    {formatExecutionTime(item.execution_time)}
                                                </td>
                                                <td className="px-5 py-3">
                                                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                                                        item.status === "success" || item.status === "completed"
                                                            ? "bg-green-100 text-green-800"
                                                            : item.status === "pending" || item.status === "processing"
                                                            ? "bg-yellow-100 text-yellow-800"
                                                            : item.status === "failed"
                                                            ? "bg-red-100 text-red-800"
                                                            : "bg-gray-100 text-gray-800"
                                                    }`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3">
                                                    {(item.status === "success" || item.status === "completed") ? (
                                                        <div className="relative inline-block group">
                                                            <button
                                                                onClick={() => { if (!item.is_downloaded) downloadByAllotmentNumber(item) }}
                                                                disabled={item.is_downloaded || downloadingFile === item.id}
                                                                title={item.is_downloaded ? "File has already been downloaded" : "Download PDF"}
                                                                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 text-sm font-medium
                                                                    ${item.is_downloaded
                                                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                        : "bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    }`}
                                                            >
                                                                {downloadingFile === item.id ? (
                                                                    <>
                                                                        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                                        </svg>
                                                                        <span>Downloading...</span>
                                                                    </>
                                                                ) : item.is_downloaded ? (
                                                                    <>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                        <span>Downloaded</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                        </svg>
                                                                        <span>Download</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                            {item.is_downloaded && (
                                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-20 shadow-lg">
                                                                    File has already been downloaded
                                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : item.status === "failed" ? (
                                                        <span className="text-xs text-red-500 font-medium">Generation failed</span>
                                                    ) : (
                                                        <span className="text-xs text-yellow-600 flex items-center gap-1.5">
                                                            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                            </svg>
                                                            <span>Processing...</span>
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Footer: pagination */}
                        <div className="px-6 py-3 border-t border-gray-100 flex-shrink-0 flex items-center justify-between bg-gray-50/50 min-h-[57px]">
                            <p className="text-xs text-gray-400">
                                {ddTotalCount > 0 && (
                                    <>
                                        Showing {(ddPage - 1) * ddLimit + 1}–{Math.min(ddPage * ddLimit, ddTotalCount)} of {ddTotalCount}
                                        {ddTotalPages > 1 && ` | Page ${ddPage} of ${ddTotalPages}`}
                                    </>
                                )}
                            </p>
                            <div className="flex justify-center mt-4">
                            <Pagination
                                count={ddTotalPages}
                                page={ddPage}
                                onChange={handleDdPageChange}
                                color="primary"
                                disabled={loadingStatus}
                                showFirstButton
                                showLastButton
                                siblingCount={1}
                                boundaryCount={1}
                                size="small"
                            />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    )
}

export default DemandDraft